import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, colors, IconGraphy, Loading, Table } from '@leapeasy/ui-kit';
import { Typography, Badge, Tooltip } from '@leapeasy/ui-kit';
import { Checkbox, Dialog, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { getDemoData } from 'utils/helpers';
import { setDocFileAction, setDocTitleAction } from 'store/actions/documentActions';
import { DocuList } from 'components/DocuList';
import ClaimForm from 'containers/ClaimForm';

// Images
import Img_1 from 'assets/img_1.png';
import { cancelPoliciesAction } from 'store/actions/propertyActions';
import { Layerbar } from 'components/common/Layerbar';
import { CancelDialog } from 'components/common/Dialog/CancelDialog';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import { getProgramAppTypeTableMeta } from 'utils/programAppTypeDisplay';

export const FlaggedCancellationTable = forwardRef(
  (
    {
      flaggedCancellations,
      dropFilter,
      filter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickCancelPolicies,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      onLoadFullDatasetTotal,
      isLoadingFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
      loading = false,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const [tableData, setTableData] = useState([]);
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
    const [checkedLows, setCheckLows] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    useEffect(() => {
      const data = [];
      if (flaggedCancellations) {
        flaggedCancellations.forEach((item, index) => {
          data.push([
            index,
            item.policy_flag || '',
            item.is_cancelled ? 'Cancelled' : 'Active',
            isDemo ? getDemoData('rider-id') : item.rider_id,
            isDemo ? getDemoData('tenant-name') : item.tenant_1_name,
            isDemo ? getDemoData('building-name') : item.apartment_building_name,
            item.coverage_start_date,
            item.coverage_end_date,
            item.app_type,
            index,
            index,
          ]);
        });

        setTableData(data);
      }
    }, [flaggedCancellations, isDemo]);

    const goDetailPage = (value) => {
      flaggedCancellations[value] && onClickCancelPolicies(flaggedCancellations[value]);
    };

    const [selectedRow, setSelectedRow] = useState(-1);
    const [docuData, setDocuData] = useState(null);
    const [cancelData, setCancelData] = useState([]);
    const [claimData, setClaimData] = useState(null);

    const handleClaimData = (row) => {
      setClaimData(flaggedCancellations[row]);
      setSelectedRow(-1);
    };

    const [isCancellingPolicies, setIsCancellingPolicies] = useState(false);

    const handleDelete = (rows) => {
      if (!rows?.length || isCancellingPolicies) return;
      const rider_ids = rows.map((row) => flaggedCancellations[row]?.rider_id).filter(Boolean);
      if (!rider_ids.length) return;
      setIsCancellingPolicies(true);
      dispatch(cancelPoliciesAction({ rider_ids, page: 'cancel-policies' }));
    };

    const successMsg = useSelector((state) => state.getIn(['notification', 'successMsg']));
    const failedMsg = useSelector((state) => state.getIn(['notification', 'failedMsg']));
    const successText = successMsg == null ? '' : String(successMsg);
    const failedText = failedMsg == null ? '' : String(failedMsg);
    const isCancelRequestInFlight = isCancellingPolicies || successText === 'loading';

    useEffect(() => {
      if (!successText || successText === 'loading') return;
      if (!successText.toLowerCase().includes('cancelled')) return;

      setIsCancellingPolicies(false);
      setIsOpenLayber(false);
      setCheckLows([]);
      setIsCheckedToCancel(false);
      setCancelData([]);
      setSelectedRow(-1);
    }, [successText]);

    useEffect(() => {
      if (!failedText || !isCancellingPolicies) return;
      setIsCancellingPolicies(false);
    }, [failedText, isCancellingPolicies]);
    const handleDownloadInsurance = (row) => {
      dispatch(setDocTitleAction(null));
      dispatch(setDocFileAction(null));
      setDocuData(flaggedCancellations[row]);
    };

    const closeDocModal = () => {
      dispatch(setDocTitleAction(null));
      dispatch(setDocFileAction(null));
      setDocuData(null);
    };
    useEffect(() => {
      window.addEventListener('click', (e) => {
        if (e.target && e.target.className && typeof e.target.className === 'string') {
          if (!e.target.className.includes('actions')) {
            setSelectedRow(-1);
          }
        }
      });
    }, []);

    const [isOpenLayber, setIsOpenLayber] = useState(false);

    const [isCheckedToCancel, setIsCheckedToCancel] = useState(false);
    const getAppTypeBadgeColor = (value) => {
      if (value === 'LDR Commercial') return 'purple';
      if (value === 'Auto Enroll') return 'tomato';
      return 'neutral';
    };
    const buildTableRow = (item, index) => [
      index,
      item.policy_flag || '',
      item.is_cancelled ? 'Cancelled' : 'Active',
      isDemo ? getDemoData('rider-id') : item.rider_id,
      isDemo ? getDemoData('tenant-name') : item.tenant_1_name,
      isDemo ? getDemoData('building-name') : item.apartment_building_name,
      item.coverage_start_date,
      item.coverage_end_date,
      item.app_type,
      index,
      index,
    ];

    return (
      <React.Fragment>
        <Dialog open={cancelData.length !== 0}>
          <CancelDialog
            img={Img_1}
            title="Cancel this policy?"
            detail="This will acknowledge cancellation for the selected policy. This action may be irreversible."
            noFunc={() => !isCancelRequestInFlight && setCancelData([])}
            yesFunc={() => handleDelete(cancelData)}
            isSubmitting={isCancelRequestInFlight}
            yesLabel="Yes, Confirm"
          />
        </Dialog>
        <Dialog open={!!docuData}>
          <DialogContainer>
            {docuData && (
              <DownloadInsuranceContainer>
                <div className="title">
                  <div>
                    <p>Download Insurance</p>
                    <IconGraphy icon="General.Clear" onClick={closeDocModal} />
                  </div>
                  <p className="desc">Explore and download insurance files.</p>
                </div>
                <DocuList data={docuData.app_id} />
              </DownloadInsuranceContainer>
            )}
          </DialogContainer>
        </Dialog>
        {claimData && (
          <Layerbar>
            <ClaimForm data={{ ...claimData }} onClose={() => setClaimData(null)} />
          </Layerbar>
        )}
        <ActionContainer>
          {checkedLows.length > 0 && <p>{checkedLows.length} row(s) selected</p>}
          <ActionButtonGroup>
            <Button
              variant="primary"
              size="large"
              disabled={checkedLows.length === 0 || isCancelRequestInFlight}
              onClick={() => {
                if (checkedLows.length > 0 && !isCancelRequestInFlight) {
                  setIsOpenLayber(true);
                }
              }}
            >
              {isCancelRequestInFlight ? (
                <ToolbarLoadingLabel>
                  <Loading size="small" />
                  Cancelling…
                </ToolbarLoadingLabel>
              ) : (
                'Cancel Policies'
              )}
            </Button>
            <Tooltip
              placement="left"
              showCarot
              variant="contained"
              title="Select one or more rows, then click Cancel Policies to proceed."
            >
              <IconGraphy
                icon="General.QuestionMark"
                width="14px"
                height="14px"
                style={{
                  cursor: 'pointer',
                  border: '1px solid #2E0F40',
                  borderRadius: '50%',
                }}
              />
            </Tooltip>
          </ActionButtonGroup>
        </ActionContainer>

        {isOpenLayber && checkedLows.length > 0 && (
          <Layerbar>
            <LayerbarContainer>
              {isCancelRequestInFlight && (
                <CancelLoadingOverlay aria-live="polite" aria-busy="true">
                  <Loading size="large" label="Cancelling policies…" />
                </CancelLoadingOverlay>
              )}
              <CancelTitle>
                <span>Policy Cancellation</span>
                <IconGraphy
                  onClick={() => !isCancelRequestInFlight && setIsOpenLayber(false)}
                  icon="General.Clear"
                  style={{
                    cursor: isCancelRequestInFlight ? 'not-allowed' : 'pointer',
                    opacity: isCancelRequestInFlight ? 0.4 : 1,
                  }}
                />
              </CancelTitle>
              <CancelBody>
                <p>
                  PLEASE BE SURE TO READ EACH STATEMENT BELOW.By checking the below box, I am
                  acknowledging that I have both read and understand each of the below statements.
                  Additionally, I understand that by proceeding with the cancellations of the
                  selected policies,
                </p>
                <ul>
                  <li>{`I am forfeiting the right to any coverage or benefits under the selected policy (or policies) for any Losses (?) that may occur after the effective date of the cancellation. This means that no claims can be made or will be honored for ANY REASON that occurs after the cancellation date.`}</li>
                  <li>{`I acknowledge that once the cancellation is confirmed, it may be irreversible, and the insurance company reserves the right to not reinstate the policy. If I wish to have insurance coverage in the future, I understand that I will need to apply for a new policy. Acceptance of a new policy is not guaranteed and is subject to the insurance company's underwriting criteria and discretion.`}</li>
                  <li>{`I acknowledge that I have thoroughly reviewed and comprehended the terms and statements outlined in this notice. I understand that by proceeding, I consent to the cancellation of the selected policy (or policies). Furthermore, I acknowledge that I have the opportunity to ask questions, dispute an invoice, or seek clarification from Leap Insurance regarding the cancellation process.fication from Leap Insurance regarding the cancellation process.`}</li>
                  <li>{`I acknowledge that the cancellation of the policy does not absolve me of any obligations to pay outstanding premiums or previous invoices associated with the policy that pre-date today's date or the effective cancellation date (whichever is earliest). All financial obligations incurred prior to the effective date of cancellation remain due and payable to the insurance company.`}</li>
                </ul>

                <CheckBoxContainer>
                  <Checkbox
                    disableRipple
                    disabled={isCancelRequestInFlight}
                    checked={isCheckedToCancel}
                    onChange={(e) => {
                      setIsCheckedToCancel(e.target.checked);
                    }}
                    sx={{
                      color: '#69236b',
                      '&.Mui-checked .MuiSvgIcon-root': {
                        borderColor: '#69236b',
                        color: '#69236b',
                      },
                    }}
                  />

                  <span>I acknowledge</span>
                </CheckBoxContainer>
              </CancelBody>
              <CancelFooter>
                <Button
                  size="medium"
                  variant="tertiary"
                  style={{ flexShrink: 2 }}
                  disabled={isCancelRequestInFlight}
                  onClick={() => setIsOpenLayber(false)}
                >
                  Discard
                </Button>
                <Button
                  size="medium"
                  variant="primary"
                  style={{ flexShrink: 2 }}
                  disabled={!isCheckedToCancel || isCancelRequestInFlight}
                  onClick={() => handleDelete([...checkedLows])}
                >
                  {isCancelRequestInFlight ? (
                    <ToolbarLoadingLabel>
                      <Loading size="small" />
                      Cancelling…
                    </ToolbarLoadingLabel>
                  ) : (
                    'Yes, Confirm'
                  )}
                </Button>
              </CancelFooter>
            </LayerbarContainer>
          </Layerbar>
        )}
        <Table
          loading={loading}
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText
                  ? 'No flagged cancellations match your search.'
                  : 'No flagged cancellations available.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          data={tableData}
          onRequestExportData={async () => {
            const exportRows = onRequestExportData
              ? await onRequestExportData()
              : flaggedCancellations;
            return (exportRows || []).map((item, index) => buildTableRow(item, index));
          }}
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const exportRows = await onRequestFullExportData();
                  return (exportRows || []).map((item, index) => buildTableRow(item, index));
                }
              : undefined
          }
          onRequestSelectedExportData={async () =>
            checkedLows
              .map((rowIndex) => flaggedCancellations[rowIndex])
              .filter(Boolean)
              .map((item, index) => buildTableRow(item, index))
          }
          selectedExportCount={checkedLows.length}
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="Flagged Cancellations"
          columns={[
            {
              name: (
                <CheckboxCell>
                  <Checkbox
                    disableRipple
                    sx={{
                      color: '#69236b',
                      padding: 0,
                      margin: 0,
                      '&.Mui-checked .MuiSvgIcon-root': {
                        borderColor: '#69236b',
                        color: '#69236b',
                      },
                    }}
                    description=""
                    label=""
                    checked={checkedLows.length === flaggedCancellations.length}
                    onChange={() => {
                      if (checkedLows.length === flaggedCancellations.length) {
                        setCheckLows([]);
                      } else {
                        setCheckLows([...Array(flaggedCancellations.length).keys()]);
                      }
                    }}
                  />
                </CheckboxCell>
              ),
              options: {
                flex: '48px 0 0',
                minWidth: 48,
                shrink: false,
                align: 'center',
                excludeFromExport: true,
                customBodyRenderer: (value) => (
                  <CheckboxCell>
                    <Checkbox
                      disableRipple
                      sx={{
                        color: '#69236b',
                        padding: 0,
                        margin: 0,
                        '&.Mui-checked .MuiSvgIcon-root': {
                          borderColor: '#69236b',
                          color: '#69236b',
                        },
                      }}
                      description=""
                      label=""
                      checked={checkedLows.includes(value)}
                      onChange={() => {
                        const tmp = [...checkedLows];
                        if (tmp.includes(value)) {
                          tmp.splice(tmp.indexOf(value), 1);
                        } else {
                          tmp.push(value);
                        }
                        setCheckLows(tmp);
                      }}
                    />
                  </CheckboxCell>
                ),
              },
              key: 'select',
            },
            {
              name: (
                <HeaderWithInfo>
                  <span>Flag</span>
                  <Tooltip
                    placement="top"
                    showCarot
                    title="Green flag indicates possible claim risk. Red flag indicates possible cancellation risk."
                  >
                    <InfoIconButton>
                      <IconGraphy icon="General.QuestionMark" width="14px" height="14px" />
                    </InfoIconButton>
                  </Tooltip>
                </HeaderWithInfo>
              ),
              options: {
                flex: '120px 1 1',
                sort: true,
                customBodyRenderer: (value) => (
                  <Tooltip title={value || 'Unknown'} placement="top" showCarot>
                    <div>
                      <Badge
                        background="rgba(243, 241, 244, 1)"
                        color="neutral"
                        label={value == 'green' ? 'Possible Claim' : 'Possible Cancellation'}
                        rounded
                        textSize="medium"
                      />
                    </div>
                  </Tooltip>
                ),
              },
              key: 'policy_flag',
            },
            {
              name: 'Policy Status',
              options: {
                flex: '60px 1 1',
                sort: true,
                customBodyRenderer: (value) => (
                  <Tooltip title={value || 'Unknown'} placement="top" showCarot>
                    <div>
                      <Badge
                        background="rgba(243, 241, 244, 1)"
                        color={value == 'Cancelled' ? 'magentaRed' : 'parisGreen'}
                        label={value}
                        rounded
                        textSize="medium"
                      />
                    </div>
                  </Tooltip>
                ),
              },
              key: 'is_cancelled',
            },
            {
              name: 'Rider ID',
              options: {
                sort: true,
              },
              key: 'rider_id',
            },
            {
              name: 'Resident Name',
              options: {
                sort: true,
              },
              key: 'tenant_1_name',
            },
            {
              name: 'Property Name',
              options: {
                filter: false,
                sort: true,
                customBodyRender: (value) => (
                  <Typography align="center" variant="body1">
                    {value.toLocaleString('en-US')}
                  </Typography>
                ),
              },
              key: 'apartment_building_name',
            },
            {
              name: 'Covera/Start Date',
              options: {
                flex: '60px 1 1',
                sort: true,
                customBodyRenderer: (value, tableMeta) => (
                  <Typography align="center" variant="body2">
                    {value.toLocaleString('en-US')}
                  </Typography>
                ),
              },
              key: 'coverage_start_date',
            },
            {
              name: 'Covera/End Date',
              options: {
                flex: '60px 1 1',
                sort: true,
                customBodyRenderer: (value, tableMeta) => (
                  <Typography align="center" variant="body2">
                    {value.toLocaleString('en-US')}
                  </Typography>
                ),
              },
              key: 'coverage_end_date',
            },
            {
              name: 'Property Type',
              options: {
                shrink: false,
                flexGrow: 1,
                sort: true,
                customBodyRenderer: (value) => {
                  const appType = getProgramAppTypeTableMeta(value, 'policy');
                  return (
                    <Tooltip title={appType.label} placement="top" showCarot>
                      <div>
                        <Badge
                          background="#F3F1F4"
                          color={getAppTypeBadgeColor(value)}
                          label={appType.label}
                          rounded
                          textSize="medium"
                        />
                      </div>
                    </Tooltip>
                  );
                },
              },
              key: 'app_type',
            },
            {
              name: 'Detail',
              options: {
                flex: '5px 1 1',
                excludeFromExport: true,
                customBodyRenderer: (value, tableMeta) => (
                  <IconGraphy
                    icon={'FileFolder.Description'}
                    style={{ color: '#702572', margin: 'auto' }}
                    onClick={() => goDetailPage(value)}
                  />
                ),
              },
              key: 'detail',
            },
            {
              name: '',
              options: {
                flex: '5px 1 1',
                excludeFromExport: true,
                customBodyRenderer: (value, tableMeta) => {
                  return (
                    <React.Fragment>
                      <DetailContainer
                        className="actions"
                        disabled={false}
                        onClick={() => setSelectedRow(value)}
                      >
                        <IconGraphy icon={'EditorLayout.MoreVert'} style={{ color: '#702572' }} />
                      </DetailContainer>
                      {selectedRow === value && (
                        <TdDiv>
                          <StyledMenuItem disabled={false} onClick={() => handleClaimData(value)}>
                            <IconGraphy icon="FileFolder.EditDocument" />
                            <span>File a Claim</span>
                          </StyledMenuItem>
                          <StyledMenuItem onClick={() => handleDownloadInsurance(value)}>
                            <IconGraphy icon="FileFolder.SimCardDownload" />
                            <span>Download Insurance</span>
                          </StyledMenuItem>
                          <StyledMenuItem disabled={false} onClick={() => setCancelData([value])}>
                            <IconGraphy icon="General.Clear" />
                            <span>Cancel Policy</span>
                          </StyledMenuItem>
                        </TdDiv>
                      )}
                    </React.Fragment>
                  );
                },
              },
              key: 'more',
            },
          ]}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Flagged Cancellations"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          onLoadFullDatasetTotal={onLoadFullDatasetTotal}
          isLoadingFullDatasetTotal={isLoadingFullDatasetTotal}
          selectedExportCount={checkedLows.length}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected
        />
      </React.Fragment>
    );
  },
);

FlaggedCancellationTable.displayName = 'FlaggedCancellationTable';

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const DetailsActionsCell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const DetailIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const DetailContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.disabled
      ? css`
          opacity: 0.5;
        `
      : css`
          cursor: pointer;
        `}
`;

const TdDiv = styled.div`
  position: absolute;
  width: 220px;
  top: 0%;
  right: 60px;
  padding: 8px;
  border-radius: 12px;
  z-index: 1;
  background-color: white;
  box-shadow: 0px 1px 3px 0px #5a5e6b1a;

  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #2e0f40;
  font-size: 14px;
  font-weight: 500 !important;
  * {
    box-sizing: border-box;
  }
  & > div {
    padding: 10px 4px;
    width: 100%;
    display: flex;
    justify-content: start;
    align-items: center;
    gap: 10px;
  }
  & > div:hover {
    background-color: #f9f8f9;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  gap: 10px;
`;

const DialogContainer = styled.div`
  padding: 20px;
  border-radius: 8px;
`;

const DownloadInsuranceContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 300px;
  * {
    box-sizing: border-box;
  }
  p {
    color: rgba(46, 15, 64, 1);
    margin: 0;
  }
  .title {
    & > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 0;
      font-size: 20px;
      font-weight: 500;
      svg {
        cursor: pointer;
      }
    }
    margin-bottom: 30px;
    border-bottom: 3px solid rgba(46, 15, 64, 0.5);
  }

  & > div:nth-of-type(2) {
    max-height: 373px;
    overflow: auto;
  }

  .desc {
    color: rgba(106, 94, 113, 1);
    font-weight: 400;
    font-size: 12px;
    margin-bottom: 10px;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 30px;
  p {
    margin: 0;
    color: rgba(144, 56, 139, 1);
    font-size: 14px;
    font-weight: bold;
    white-space: nowrap;
  }

  button {
    font-size: 14px;
    width: 170px;
  }
`;

const ActionButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CancelTitle = styled.div`
  width: 100%;
  display: flex;
  padding: 16px 0;
  border-bottom: 1px solid rgba(234, 234, 234, 1);
  align-items: center;
  justify-content: space-between;
  color: rgba(46, 15, 64, 1);
  font-size: 20px;
  font-weight: 500;

  svg {
    cursor: pointer;
  }
`;

const LayerbarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CancelLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.82);
  border-radius: 8px;
`;

const ToolbarLoadingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CancelBody = styled.div`
  color: rgba(106, 94, 113, 1);
  font-weight: 400;
  font-size: 14px;
  margin-top: 24px;
  flex: 1;
  width: 100%;
  overflow: auto;
  ul {
    padding: 20px;
    margin: 0;
  }
  p {
    margin: 0;
  }
`;
const CancelFooter = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 24px;
  gap: 20px;
`;

const CheckBoxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 14px;
  font-weight: 600;
  color: rgba(46, 15, 64, 1);
`;

const HeaderWithInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const InfoIconButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #2e0f40;
  border-radius: 999px;
  width: 16px;
  height: 16px;
  line-height: 0;
  cursor: pointer;
`;
